import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

type DirtyMap = Record<string, boolean>;

type FlowDirtyContextValue = {
  isDirtyForWorkflow: (workflowId: string | undefined | null) => boolean;
  setWorkflowDirty: (workflowId: string | undefined | null, dirty: boolean) => void;
  clearWorkflowDirty: (workflowId: string | undefined | null) => void;
};

const FlowDirtyContext = createContext<FlowDirtyContextValue | null>(null);

export function FlowDirtyProvider({ children }: { children: React.ReactNode }) {
  const [dirtyByWorkflowId, setDirtyByWorkflowId] = useState<DirtyMap>({});

  const isDirtyForWorkflow = useCallback(
    (workflowId: string | undefined | null) => {
      if (!workflowId) return false;
      return Boolean(dirtyByWorkflowId[String(workflowId)]);
    },
    [dirtyByWorkflowId],
  );

  const setWorkflowDirty = useCallback((workflowId: string | undefined | null, dirty: boolean) => {
    if (!workflowId) return;
    const id = String(workflowId);
    setDirtyByWorkflowId((prev) => {
      const next = { ...(prev || {}) };
      if (dirty) {
        next[id] = true;
      } else {
        delete next[id];
      }
      return next;
    });
  }, []);

  const clearWorkflowDirty = useCallback((workflowId: string | undefined | null) => {
    if (!workflowId) return;
    const id = String(workflowId);
    setDirtyByWorkflowId((prev) => {
      if (!prev || !Object.prototype.hasOwnProperty.call(prev, id)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const value = useMemo<FlowDirtyContextValue>(
    () => ({
      isDirtyForWorkflow,
      setWorkflowDirty,
      clearWorkflowDirty,
    }),
    [isDirtyForWorkflow, setWorkflowDirty, clearWorkflowDirty],
  );

  return <FlowDirtyContext.Provider value={value}>{children}</FlowDirtyContext.Provider>;
}

export function useFlowDirty() {
  const ctx = useContext(FlowDirtyContext);
  if (!ctx) {
    throw new Error("useFlowDirty must be used within <FlowDirtyProvider>");
  }
  return ctx;
}
